import useMakeTransfer from 'hooks/Bank/useMakeTransfer';
import { type FC } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  MoneyTransferInitialData,
  TransferData,
  TransferFormFields,
  TransferReviewData,
  TransferStepProps,
} from '../types';
import type { Connector, Transaction } from 'types/transactions.types';
import { transformFormDataToReviewData } from '../utils/dataTransforms';
import ExternalTransfer, {
  type ExternalInitialPaymentData,
} from './ExternalTransfer.component';
import InternalTransferComponent from './InternalTransfer.component';

interface TransferFormStepProps extends TransferStepProps {
  sourceAccount: Connector;
  receiptAccount: Connector | 'external';
  initialData?: MoneyTransferInitialData;
  isInternalTransfer?: boolean;
  onTransferSuccess?: (transaction?: Transaction) => void;
  isStandingOrderPayment?: boolean;
  onReview?: (data: TransferReviewData) => void;
  initialTransferData?: TransferData;
  preservedFormData?: TransferFormFields;
}
const TransferFormStep: FC<TransferFormStepProps> = ({
  sourceAccount,
  receiptAccount,
  initialData,
  isInternalTransfer,
  onTransferSuccess,
  isStandingOrderPayment,
  onReview,
  preservedFormData,
}) => {
  const { t } = useTranslation();
  const { mutate, isLoading } = useMakeTransfer({
    withRedirection: true,
    onTransferSuccess,
  });

  // For internal transfers, handle them with review step
  if (isInternalTransfer && receiptAccount !== 'external') {
    const handleInternalReview = (reviewData: TransferReviewData) => {
      // Pass review data to the parent component
      onReview?.(reviewData);
    };

    return (
      <InternalTransferComponent
        sourceAccount={sourceAccount}
        destinationAccount={receiptAccount}
        initialAmount={initialData?.amount}
        onTransferSuccess={onTransferSuccess}
        onReview={handleInternalReview}
        showReviewStep
        withRedirection
      />
    );
  }

  // Handle external transfer form submission
  const handleExternalTransferSubmit = (formData: Record<string, any>) => {
    // Prepare review data for external transfers using the transformer
    const reviewData = transformFormDataToReviewData(
      formData,
      mutate,
      isLoading,
      sourceAccount._id,
      undefined, // No destination connector for external transfers
    );
    onReview?.(reviewData);
  };

  // // Use preserved form data if available, otherwise use initial amount
  const preparedData = {
    ...(initialData && {
      recipient_iban: initialData.recipient_iban,
      recipient_name: initialData.recipient_name,
      communication: initialData.communication,
      amount: initialData.amount,
    }),
    // Override with preserved form data if user has made changes
    ...(preservedFormData && preservedFormData),
  };

  return (
    <div className="w-full px-6">
      <ExternalTransfer
        initialData={preparedData as ExternalInitialPaymentData}
        onSubmit={handleExternalTransferSubmit}
        sourceAccountBalance={sourceAccount?.balance?.amount}
        isStandingOrderPayment={isStandingOrderPayment}
        btnLabel={t('accountable_banking.review_transfer.title')}
        sourceAccount={sourceAccount}
        formType="external"
      />
    </div>
  );
};

export default TransferFormStep;
